import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {TextStyleOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-styling-options/base-styling-options.types';
import {useUpdateEffect} from 'ahooks';
import {StyleThumbnailOptions} from '@Panels/poster-editor-item-font-style-panel/components/style-thumbnail-options';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromTextStylesActiveItem} from '@Hooks/poster-editor/useGetKeyFromTextStylesActiveItem';
import {useGetKeyFromTableItem} from '@Hooks/poster-editor/useGetKeyFromTableItem';

interface PosterEditorItemStyle2PanelProps {
  panelId: string;
}

export default function PosterEditorItemStyle2Panel({...props}: PosterEditorItemStyle2PanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const itemStylesStroke = useGetKeyFromTextStylesActiveItem('stroke');
  const itemIsBoldTwo = useGetKeyFromTableItem('isBold2');
  const itemIsItalicTwo = useGetKeyFromTableItem('isItalic2');
  const itemLinethroughTwo = useGetKeyFromTableItem('lineThrough2');
  const itemUnderlineTwo = useGetKeyFromTableItem('underLine2');

  useUpdateEffect((): void => {
    onClose();
  }, [activeItem]);

  if (!activeItem || (!activeItem?.isTableItem() && !activeItem.isMenu())) {
    return null;
  }

  if (itemIsBoldTwo === undefined || itemLinethroughTwo === undefined || itemUnderlineTwo === undefined || itemIsItalicTwo === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_style_two')} />;
  };

  const applyStyle = (type: TextStyleOptions): void => {
    switch (type) {
      case TextStyleOptions.BOLD: {
        activeItem.toggleBold2();
        return;
      }
      case TextStyleOptions.ITALIC: {
        activeItem.toggleItalic2();
        return;
      }
      case TextStyleOptions.UNDERLINE: {
        activeItem.toggleUnderline2();
        return;
      }
      case TextStyleOptions.STRIKE_THROUGH: {
        activeItem.toggleLineThrough2();
        return;
      }
      default:
        throw new Error('unhandled text styling option');
    }
  };

  const getContent = (): ReactElement => {
    return (
      <StyleThumbnailOptions
        isBold={itemIsBoldTwo}
        isItalic={itemIsItalicTwo}
        isUnderline={itemUnderlineTwo}
        isLineThrough={itemLinethroughTwo}
        applyStyle={applyStyle}
        isBoldDisabled={itemStylesStroke}
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
